import cn from 'classnames';
import React from 'react';

import {
  countSlots,
  isSlotFormat,
  MAX_SLOT_COLUMNS,
  MAX_SLOT_ROWS,
} from 'editor/src/store/design/util/slotLayout/computeSlotFrames';

import useTr from 'editor/src/util/useTr';

import Button from 'editor/src/component/Button';

import styles from './index.module.scss';

interface Props {
  rows: number[][];
  onChange: (rows: number[][]) => void;
  disabled?: boolean;
}

const MIN_ROWS = 1;

/**
 * One line per product count; each cell is a row of the page and clicking it cycles the row
 * through one, two, three slots and empty. A count whose rows do not place exactly that many
 * products is flagged, since the buyer would get a layout with a hole or an extra product.
 */
function LayoutsGrid({ rows, onChange, disabled }: Props) {
  const tr = useTr();
  const rowCount = Math.max(1, ...rows.map((countRows) => countRows.length));

  const cycle = (countIndex: number, row: number) => {
    const next = rows.map((countRows) => [...countRows]);
    next[countIndex][row] = (next[countIndex][row] + 1) % (MAX_SLOT_COLUMNS + 1);
    onChange(next);
  };

  const setRowCount = (count: number) => {
    const target = Math.min(MAX_SLOT_ROWS, Math.max(MIN_ROWS, count));
    onChange(
      rows.map((countRows) =>
        Array.from({ length: target }, (_, row) => (row < countRows.length ? countRows[row] : 0)),
      ),
    );
  };

  return (
    <div className={styles.grid}>
      <div className={styles.rowCount}>
        <span>{tr('editor-slot-layout-rows', 'Rows on the page')}</span>
        <Button
          secondary
          small
          disabled={disabled || rowCount <= MIN_ROWS}
          ariaLabel={tr('editor-slot-layout-rows-fewer', 'One row fewer')}
          onClick={() => setRowCount(rowCount - 1)}
        >
          −
        </Button>
        <span className={styles.rowCountValue}>{rowCount}</span>
        <Button
          secondary
          small
          disabled={disabled || rowCount >= MAX_SLOT_ROWS}
          ariaLabel={tr('editor-slot-layout-rows-more', 'One row more')}
          onClick={() => setRowCount(rowCount + 1)}
        >
          +
        </Button>
      </div>
      {rows.map((countRows, countIndex) => {
        const count = countIndex + 1;
        const placed = countSlots(countRows);
        const ok = placed === count;
        return (
          <div key={count} className={cn(styles.count, 'cy-slot-layout-count', { [styles.mismatch]: !ok })}>
            <div className={styles.countLabel}>
              {count === 1
                ? tr('editor-slot-layout-count-product', '1 product')
                : tr('editor-slot-layout-count-products', '{{count}} products', { count })}
            </div>
            <div className={styles.page}>
              {Array.from({ length: rowCount }, (_, row) => {
                const columns = countRows[row] ?? 0;
                return (
                  <button
                    key={row}
                    type="button"
                    disabled={disabled}
                    className={cn(styles.row, 'cy-slot-layout-row', { [styles.empty]: !isSlotFormat(columns) })}
                    onClick={() => cycle(countIndex, row)}
                    aria-label={
                      (columns === 0 && tr('editor-slot-layout-row-empty', 'Empty row')) ||
                      (columns === 1 && tr('editor-slot-layout-row-slot', '1 slot in this row')) ||
                      tr('editor-slot-layout-row-slots', '{{count}} slots in this row', { count: columns })
                    }
                  >
                    {Array.from({ length: columns }, (_, column) => (
                      <span key={column} className={styles.slot} />
                    ))}
                  </button>
                );
              })}
            </div>
            <div className={styles.status}>
              {ok && placed === 1 && tr('editor-slot-layout-count-ok-one', '1 slot')}
              {ok && placed !== 1 && tr('editor-slot-layout-count-ok', '{{count}} slots', { count: placed })}
              {!ok && placed === 1 && tr('editor-slot-layout-count-mismatch-one', '1 slot, needs {{count}}', { count })}
              {!ok &&
                placed !== 1 &&
                tr('editor-slot-layout-count-mismatch', '{{placed}} slots, needs {{count}}', { placed, count })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default React.memo(LayoutsGrid);
